import { useCallback, useEffect, useRef } from 'react'

import { EMPTY_FUNCTION, EMPTY_OBJECT } from '../../constants/common'
import { isValueStringType } from '../../utils/common'
import { useInitData } from '../useInitData'

import {
  DEEPGRAM_AUTH_SUBPROTOCOL,
  DeepgramWebSocketError,
  WEBSOCKET_NORMAL_CLOSE_CODE,
} from './useDeepgramWebSocket.constants'
import {
  getDeepgramUrl,
  isWebSocketReadyStateConnecting,
  isWebSocketReadyStateOpen,
} from './useDeepgramWebSocket.utils'

export function useDeepgramWebSocket({
  authSubprotocol = DEEPGRAM_AUTH_SUBPROTOCOL,
  onClose = EMPTY_FUNCTION,
  onError = EMPTY_FUNCTION,
  onMessage = EMPTY_FUNCTION,
  onOpen = EMPTY_FUNCTION,
} = EMPTY_OBJECT) {
  const { deepgram_api_key: apiKey } = useInitData()

  const callbacksRef = useRef({ onClose, onError, onMessage, onOpen })

  // Latest-ref sync (intentionally no deps). Consumer callbacks are typically
  // inline, so their identities change every render — listing them as deps
  // would re-run this effect every render anyway. Omitting the array makes the
  // "sync on every commit" intent explicit. WebSocket event handlers read these
  // through the ref so they always invoke the latest consumer callbacks
  // without forcing connect/disconnect to be recreated.
  useEffect(() => {
    const callbacks = callbacksRef.current

    callbacks.onClose = onClose
    callbacks.onError = onError
    callbacks.onMessage = onMessage
    callbacks.onOpen = onOpen
  })

  const wsRef = useRef(null)
  // Bumped on each connect()/disconnect(); async closures bail when the token shifts.
  const sessionTokenRef = useRef(0)

  const disconnect = useCallback(() => {
    sessionTokenRef.current += 1

    const ws = wsRef.current

    wsRef.current = null

    if (ws) {
      ws.onopen = null
      ws.onmessage = null
      ws.onerror = null
      ws.onclose = null

      try {
        if (
          isWebSocketReadyStateOpen(ws.readyState) ||
          isWebSocketReadyStateConnecting(ws.readyState)
        ) {
          ws.close(WEBSOCKET_NORMAL_CLOSE_CODE)
        }
      } catch {
        // WebSocket.close() may throw if the connection is already torn down.
      }
    }
  }, [])

  const connect = useCallback(
    (url, query) => {
      if (wsRef.current) {
        disconnect()
      }

      // The WebSocket constructor throws on non-string / empty protocol tokens,
      // so check the apiKey up front and surface a distinct config error instead
      // of conflating it with network failures in the catch below.
      if (!isValueStringType(apiKey) || !apiKey) {
        callbacksRef.current.onError(DeepgramWebSocketError.ConfigError)

        return
      }

      const sessionToken = ++sessionTokenRef.current

      let ws = null

      try {
        ws = new WebSocket(getDeepgramUrl(url, query), [
          authSubprotocol,
          apiKey,
        ])
      } catch {
        callbacksRef.current.onError(DeepgramWebSocketError.ConnectionError)
      }

      if (ws) {
        wsRef.current = ws

        ws.onopen = () => {
          if (sessionTokenRef.current === sessionToken) {
            callbacksRef.current.onOpen()
          }
        }

        ws.onmessage = event => {
          if (sessionTokenRef.current === sessionToken) {
            try {
              const payload = JSON.parse(event.data)

              callbacksRef.current.onMessage(payload)
            } catch {
              // Ignore non-JSON frames.
            }
          }
        }

        ws.onerror = () => {
          if (
            sessionTokenRef.current === sessionToken &&
            !isWebSocketReadyStateOpen(ws.readyState)
          ) {
            callbacksRef.current.onError(DeepgramWebSocketError.ConnectionError)
          }
          // After open, onclose carries code/reason and handles error mapping.
        }

        ws.onclose = closeEvent => {
          if (sessionTokenRef.current === sessionToken) {
            callbacksRef.current.onClose(closeEvent)
          }
        }
      }
    },
    [apiKey, authSubprotocol, disconnect]
  )

  const send = useCallback(data => {
    const ws = wsRef.current

    if (ws && isWebSocketReadyStateOpen(ws.readyState)) {
      try {
        ws.send(data)

        return true
      } catch {
        // WebSocket.send() may throw if the socket closed between the
        // readyState check and the send call.
        return false
      }
    }

    return false
  }, [])

  const disconnectRef = useRef(disconnect)

  useEffect(() => {
    disconnectRef.current = disconnect
  }, [disconnect])

  // Bind teardown to mount/unmount only — reading `disconnect` through a ref
  // keeps this effect from re-firing if its dep graph changes.
  useEffect(() => {
    return () => {
      disconnectRef.current()
    }
  }, [])

  return {
    connect,
    disconnect,
    send,
  }
}
